import { z } from "zod";
import { StatesMap } from "../constants/form-fields";
import { RulesPatterns } from "./validations";
const requiredSelectField = z
    .string()
    .min(1, { message: "This field is required." })
    .refine((val) => val !== "", {
    message: "This field is required.",
});
const optionalField = z.string().optional();
const requiredTextField = z.string().trim().nonempty("This field is required.");
export const userSchema = z.object({
    firstName: requiredTextField
        .regex(RulesPatterns.invalidCharacters, "This name is not valid. Names can include only letters (A-Z and a-z), numbers (0-9), dashes (-), and periods (.).")
        .max(64, "Must be 64 characters or less"),
    lastName: requiredTextField
        .regex(RulesPatterns.invalidCharacters, "This name is not valid. Names can include only letters (A-Z and a-z), numbers (0-9), dashes (-), and periods (.).")
        .max(64, "Must be 64 characters or less"),
});
export const communitySchema = z
    .object({
    name: z
        .string()
        .min(1, "This field is required.")
        .max(64, "Must be 64 characters or less"),
    headline: optionalField,
    bio: z
        .string()
        .regex(RulesPatterns.shortBioInvalidCharacters, "This field contains invalid characters.")
        .optional()
        .or(z.literal("")),
    displayLocation: z.object({
        countryRegion: optionalField,
        stateProvince: optionalField,
    }),
    socials: z.array(z
        .object({
        option: z.string().min(1, "This field is required."),
        input: z.string().min(1, "This field is required."),
    })
        .superRefine((data, ctx) => {
        const { option, input } = data;
        switch (option.toLowerCase()) {
            case "linkedin":
                if (!RulesPatterns.linkedInUrlDomain.test(input)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["input"],
                        message: "invalid link",
                    });
                }
                break;
            case "blog":
            case "repost":
                if (!RulesPatterns.username.test(input) &&
                    !RulesPatterns.url.test(input)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["input"],
                        message: "invalid link",
                    });
                }
                break;
            default:
                if (!RulesPatterns.url.test(input)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["input"],
                        message: "invalid link",
                    });
                }
        }
    })),
})
    .superRefine((data, ctx) => {
    if (data.displayLocation?.countryRegion &&
        !!StatesMap[data.displayLocation.countryRegion] &&
        !data.displayLocation.stateProvince?.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required.",
            path: ["displayLocation.stateProvince"],
        });
    }
});
export const emailsSchema = z.object({
    email: requiredTextField
        .regex(RulesPatterns.email, "Invalid email address")
        .regex(RulesPatterns.invalidEmailCharacters, "Invalid email address")
        .max(100, "Must be 100 characters or less"),
    type: requiredTextField,
    tag: z.record(z.any()).optional(),
});
export const additionalEmailsSchema = z.object({
    emails: z.array(emailsSchema),
});
export const workSchema = z.object({
    communications: z.object({
        phone: requiredTextField.regex(RulesPatterns.phoneNumber, "Must be 7-15 digits."),
    }),
    job: z.object({
        title: requiredTextField.max(64, "Must be 64 characters or less"),
    }),
    location: z.object({
        city: requiredTextField.max(64, "Must be 100 characters or less"),
        postalCode: requiredTextField.regex(RulesPatterns.postalCode, "This field contains invalid characters."),
    }),
    company: z.object({
        name: requiredTextField.max(255, "Must be 64 characters or less"),
        size: requiredSelectField,
        type: optionalField,
    }),
    engagement: z.object({
        projectTimeFrame: optionalField,
    }),
});
export const interestsSchema = z
    .object({
    interests: z.array(z.string()).optional(),
    location: z.object({
        countryRegion: requiredSelectField,
        stateProvince: optionalField,
    }),
    job: z.object({
        industry: requiredSelectField,
        role: requiredSelectField,
    }),
    engagement: z.object({
        usageLevel: requiredSelectField,
        useCase: requiredSelectField,
    }),
    communications: z.object({
        koreanComplianceOptIn: z.boolean().optional(),
    }),
})
    .superRefine((data, ctx) => {
    if (!!StatesMap[data.location.countryRegion] &&
        !data.location.stateProvince?.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required.",
            path: ["location.stateProvince"],
        });
    }
    if (data.location.countryRegion === "KR") {
        if (data.communications?.koreanComplianceOptIn !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "This field is required.",
                path: ["communications.koreanComplianceOptIn"],
            });
        }
    }
});
//# sourceMappingURL=schemas.js.map